/**
 * The Kolmogorov–Smirnov test: one sample against a fully specified distribution, and two
 * samples against each other.
 *
 * One sample: with F the hypothesised CDF and x₍₁₎ ≤ … ≤ x₍ₙ₎ the sorted data,
 *
 *   D⁺ = max(i/n − F(x₍ᵢ₎)),  D⁻ = max(F(x₍ᵢ₎) − (i − 1)/n),  D = max(D⁺, D⁻).
 *
 * Two samples: D = max |F̂ₘ − Ĝₙ| over the pooled values, with D⁺ = max(F̂ₘ − Ĝₙ) and
 * D⁻ = max(Ĝₙ − F̂ₘ).
 *
 * Alternatives follow R's `ks.test`: "greater" means the CDF of the data (or of the first
 * sample) lies above the reference (or the second sample's), statistic D⁺; "less" is the
 * reverse, statistic D⁻.
 *
 * p-values:
 * - One-sample two-sided: exact P(Dₙ ≥ d) from Marsaglia, Tsang & Wang (2003), "Evaluating
 *   Kolmogorov's distribution" — the algorithm in R's `ks.test` — for n ≤ 500. Where
 *   1 − P has lost its digits (a far tail) or the matrix would be unwieldy, twice the exact
 *   one-sided value is used instead, which is accurate because the tails are then disjoint.
 *   Beyond n = 500 the limiting Kolmogorov distribution with λ = √n·D is used.
 * - One-sample one-sided: exact Birnbaum & Tingey (1951),
 *   P(D⁺ ≥ d) = d · Σⱼ C(n, j)(1 − d − j/n)^(n−j)(d + j/n)^(j−1), j ≤ n(1 − d);
 *   asymptotically exp(−2nd²).
 * - Two-sample: exact, by counting lattice paths of the pooled ordering that stay inside the
 *   band (Hodges 1958; with ties, the band is enforced only where the pooled value changes,
 *   Schröer & Trenkler 1995 — as R ≥ 4.2 does); asymptotically the Kolmogorov limit with
 *   λ = √(mn/(m + n))·D.
 *
 * Pinned against R 4.5.0 `ks.test` in `stats-tests-more-ks.test.ts`.
 */

import { mean } from "@/lib/stats/descriptive";
import { normalCdf } from "@/lib/stats/distributions";
import { logChoose } from "@/lib/stats/special";
import type { Alternative } from "@/lib/tool-kit/stats-tests/format";

/* ----------------------------------------------- Kolmogorov distribution (exact) */

function multiply(a: Float64Array, b: Float64Array, m: number): Float64Array {
  const c = new Float64Array(m * m);
  for (let i = 0; i < m; i++) {
    for (let j = 0; j < m; j++) {
      let sum = 0;
      for (let k = 0; k < m; k++) {
        sum += a[i * m + k] * b[k * m + j];
      }
      c[i * m + j] = sum;
    }
  }
  return c;
}

/** A^n by repeated squaring, tracking a base-10 exponent to avoid overflow. */
function matrixPower(
  a: Float64Array,
  ea: number,
  m: number,
  n: number
): { v: Float64Array; ev: number } {
  if (n === 1) {
    return { v: Float64Array.from(a), ev: ea };
  }
  const half = matrixPower(a, ea, m, Math.floor(n / 2));
  const squared = multiply(half.v, half.v, m);
  const eb = 2 * half.ev;
  let v: Float64Array;
  let ev: number;
  if (n % 2 === 0) {
    v = squared;
    ev = eb;
  } else {
    v = multiply(a, squared, m);
    ev = ea + eb;
  }
  const centre = v[Math.floor(m / 2) * m + Math.floor(m / 2)];
  if (centre > 1e140) {
    for (let i = 0; i < v.length; i++) {
      v[i] *= 1e-140;
    }
    ev += 140;
  }
  return { v, ev };
}

/** One entry of the matrix H: the band, its boundary corrections, and the factorial scaling. */
function hEntry(i: number, j: number, h: number, m: number): number {
  let entry = i - j + 1 < 0 ? 0 : 1;
  if (j === 0) {
    entry -= h ** (i + 1);
  }
  if (i === m - 1) {
    entry -= h ** (m - j);
  }
  if (i === m - 1 && j === 0) {
    entry += 2 * h - 1 > 0 ? (2 * h - 1) ** m : 0;
  }
  for (let g = 1; g <= i - j + 1; g++) {
    entry /= g;
  }
  return entry;
}

/** The (2k − 1) × (2k − 1) matrix H of Marsaglia, Tsang & Wang, with k = ⌊nd⌋ + 1. */
function hMatrixFor(
  n: number,
  d: number
): { h: Float64Array; k: number; m: number } {
  const k = Math.floor(n * d) + 1;
  const m = 2 * k - 1;
  const h = k - n * d;
  const matrix = new Float64Array(m * m);
  for (let i = 0; i < m; i++) {
    for (let j = 0; j < m; j++) {
      matrix[i * m + j] = hEntry(i, j, h, m);
    }
  }
  return { h: matrix, k, m };
}

/** P(Dₙ < d), the two-sided Kolmogorov–Smirnov distribution function (Marsaglia–Tsang–Wang). */
export function kolmogorovExactCdf(n: number, d: number): number {
  const { h, k, m } = hMatrixFor(n, d);
  const { v, ev } = matrixPower(h, 0, m, n);
  let s = v[(k - 1) * m + k - 1];
  let exponent = ev;
  for (let i = 1; i <= n; i++) {
    s = (s * i) / n;
    if (s < 1e-140) {
      s *= 1e140;
      exponent -= 140;
    }
  }
  return s * 10 ** exponent;
}

/** The limiting distribution: P(√n·Dₙ ≥ λ) = 2 Σ (−1)^(k−1) exp(−2k²λ²). */
export function kolmogorovLimitUpper(lambda: number): number {
  if (lambda <= 0) {
    return 1;
  }
  if (lambda < 1) {
    let sum = 0;
    for (let k = 1; k <= 20; k++) {
      sum += Math.exp(-(((2 * k - 1) * Math.PI) ** 2) / (8 * lambda * lambda));
    }
    return Math.max(0, 1 - (Math.sqrt(2 * Math.PI) / lambda) * sum);
  }
  let sum = 0;
  for (let k = 1; k <= 100; k++) {
    const term = Math.exp(-2 * k * k * lambda * lambda);
    sum += (k % 2 === 1 ? 1 : -1) * term;
    if (term < 1e-300) {
      break;
    }
  }
  return Math.min(1, 2 * sum);
}

export const EXACT_ONE_SAMPLE_LIMIT = 500;
const MAX_MATRIX = 201;
/** Below this, 1 − P(Dₙ < d) has lost too many digits to trust. */
const FAR_TAIL = 1e-8;

export type KsMethod = "exact" | "asymptotic";

/** Exact one-sided p-value P(D⁺ ≥ d) of Birnbaum & Tingey. */
export function oneSidedExactP(d: number, n: number): number {
  if (d <= 0) {
    return 1;
  }
  if (d >= 1) {
    return 0;
  }
  let sum = 0;
  const top = Math.floor(n * (1 - d));
  for (let j = 0; j <= top; j++) {
    const base = 1 - d - j / n;
    if (base <= 0 && n - j > 0) {
      continue;
    }
    sum += Math.exp(
      logChoose(n, j) + (n - j) * Math.log(base) + (j - 1) * Math.log(d + j / n)
    );
  }
  return Math.min(1, d * sum);
}

/** The p-value of a one-sample statistic, and how it was obtained. */
export function oneSampleP(
  d: number,
  n: number,
  alternative: Alternative
): { p: number; method: KsMethod } {
  if (alternative !== "two-sided") {
    if (n <= EXACT_ONE_SAMPLE_LIMIT) {
      return { p: oneSidedExactP(d, n), method: "exact" };
    }
    return { p: Math.exp(-2 * n * d * d), method: "asymptotic" };
  }
  const matrixSize = 2 * (Math.floor(n * d) + 1) - 1;
  if (n <= EXACT_ONE_SAMPLE_LIMIT && matrixSize <= MAX_MATRIX) {
    const exact = 1 - kolmogorovExactCdf(n, d);
    // 1 − P loses its digits in the far tail; there the two one-sided tails are disjoint
    // (or nearly so), and Birnbaum–Tingey's exact one-sided value is accurate.
    if (exact > FAR_TAIL) {
      return { p: Math.min(1, exact), method: "exact" };
    }
    return { p: Math.min(1, 2 * oneSidedExactP(d, n)), method: "exact" };
  }
  if (n <= EXACT_ONE_SAMPLE_LIMIT) {
    return { p: Math.min(1, 2 * oneSidedExactP(d, n)), method: "exact" };
  }
  return { p: kolmogorovLimitUpper(Math.sqrt(n) * d), method: "asymptotic" };
}

/* ---------------------------------------------------------- reference distributions */

export type Reference =
  | { kind: "normal"; mean: number; sd: number }
  | { kind: "uniform"; min: number; max: number }
  | { kind: "exponential"; rate: number };

export function referenceCdf(reference: Reference, x: number): number {
  if (reference.kind === "normal") {
    return normalCdf(x, reference.mean, reference.sd);
  }
  if (reference.kind === "uniform") {
    return Math.min(
      1,
      Math.max(0, (x - reference.min) / (reference.max - reference.min))
    );
  }
  return x <= 0 ? 0 : 1 - Math.exp(-reference.rate * x);
}

/* ------------------------------------------------------------------ one sample */

export interface KsOneSampleResult {
  n: number;
  dPlus: number;
  dMinus: number;
  /** The statistic for the chosen alternative. */
  d: number;
  alternative: Alternative;
  p: number;
  method: KsMethod;
  /** Sorted data, with its ECDF just after each value. */
  sorted: number[];
  /** Where the largest gap occurs, with the reference CDF and the ECDF there. */
  atX: number;
  cdfAtX: number;
  ecdfAtX: number;
  hasTies: boolean;
}

export type KsOutcome<R> =
  | { ok: true; result: R }
  | { ok: false; reason: "too-few-values" | "invalid-reference" };

export function ksOneSample(
  values: readonly number[],
  reference: Reference,
  alternative: Alternative = "two-sided"
): KsOutcome<KsOneSampleResult> {
  const n = values.length;
  if (n < 1) {
    return { ok: false, reason: "too-few-values" };
  }
  if (
    (reference.kind === "normal" && !(reference.sd > 0)) ||
    (reference.kind === "uniform" && !(reference.max > reference.min)) ||
    (reference.kind === "exponential" && !(reference.rate > 0))
  ) {
    return { ok: false, reason: "invalid-reference" };
  }
  const sorted = [...values].sort((a, b) => a - b);
  let dPlus = 0;
  let dMinus = 0;
  let atPlus = sorted[0];
  let atMinus = sorted[0];
  for (const [index, x] of sorted.entries()) {
    const f = referenceCdf(reference, x);
    const up = (index + 1) / n - f;
    const down = f - index / n;
    if (up > dPlus) {
      dPlus = up;
      atPlus = x;
    }
    if (down > dMinus) {
      dMinus = down;
      atMinus = x;
    }
  }
  const d = {
    "two-sided": Math.max(dPlus, dMinus),
    greater: dPlus,
    less: dMinus,
  }[alternative];
  const { p, method } = oneSampleP(d, n, alternative);
  const useMinus =
    alternative === "less" || (alternative === "two-sided" && dMinus > dPlus);
  const location = useMinus ? atMinus : atPlus;
  const below = sorted.filter((x) => x < location).length;
  const upTo = sorted.filter((x) => x <= location).length;
  return {
    ok: true,
    result: {
      n,
      dPlus,
      dMinus,
      d,
      alternative,
      p: Math.min(1, Math.max(0, p)),
      method,
      sorted,
      atX: location,
      cdfAtX: referenceCdf(reference, location),
      ecdfAtX: useMinus ? below / n : upTo / n,
      hasTies: new Set(sorted).size < n,
    },
  };
}

/* ------------------------------------------------------------------ two samples */

export const EXACT_TWO_SAMPLE_CELLS = 2_000_000;

export interface KsTwoSampleResult {
  m: number;
  n: number;
  dPlus: number;
  dMinus: number;
  d: number;
  alternative: Alternative;
  p: number;
  method: KsMethod;
  hasTies: boolean;
  /** The pooled value where the largest gap occurs and both ECDFs there. */
  atX: number;
  ecdfFirst: number;
  ecdfSecond: number;
}

/** Probability that the pooled ordering never leaves the band, over all arrangements. */
function latticeInsideProbability(
  q: number,
  m: number,
  n: number,
  blockEnd: readonly boolean[],
  alternative: Alternative
): number {
  const violates = (i: number, j: number): boolean => {
    if (!blockEnd[i + j]) {
      return false;
    }
    const gap = i / m - j / n;
    if (alternative === "two-sided") {
      return Math.abs(gap) > q;
    }
    return alternative === "greater" ? gap > q : -gap > q;
  };
  const u = new Float64Array(n + 1);
  for (let j = 0; j <= n; j++) {
    u[j] = violates(0, j) ? 0 : 1;
  }
  for (let i = 1; i <= m; i++) {
    const w = i / (i + n);
    u[0] = violates(i, 0) ? 0 : w * u[0];
    for (let j = 1; j <= n; j++) {
      u[j] = violates(i, j) ? 0 : w * u[j] + u[j - 1];
    }
  }
  return u[n];
}

interface PooledScan {
  dPlus: number;
  dMinus: number;
  atPlus: number;
  atMinus: number;
  /** blockEnd[t]: the t-th pooled value is the last of a run of equal values. */
  blockEnd: boolean[];
  hasTies: boolean;
}

/** Walk the pooled sample once, comparing both ECDFs wherever the pooled value changes. */
function scanPooled(
  first: readonly number[],
  second: readonly number[]
): PooledScan {
  const m = first.length;
  const n = second.length;
  const total = m + n;
  const pooled = [...first, ...second].sort((a, b) => a - b);
  const sortedFirst = [...first].sort((a, b) => a - b);
  const sortedSecond = [...second].sort((a, b) => a - b);
  const blockEnd: boolean[] = new Array<boolean>(total + 1).fill(true);
  const scan: PooledScan = {
    dPlus: 0,
    dMinus: 0,
    atPlus: pooled[0],
    atMinus: pooled[0],
    blockEnd,
    hasTies: false,
  };
  let i = 0;
  let j = 0;
  for (let t = 0; t < total; t++) {
    const x = pooled[t];
    while (i < m && sortedFirst[i] <= x) {
      i += 1;
    }
    while (j < n && sortedSecond[j] <= x) {
      j += 1;
    }
    const isEnd = t === total - 1 || pooled[t + 1] !== x;
    blockEnd[t + 1] = isEnd;
    scan.hasTies ||= !isEnd;
    if (!isEnd) {
      continue;
    }
    const gap = i / m - j / n;
    if (gap > scan.dPlus) {
      scan.dPlus = gap;
      scan.atPlus = x;
    }
    if (-gap > scan.dMinus) {
      scan.dMinus = -gap;
      scan.atMinus = x;
    }
  }
  return scan;
}

export function ksTwoSample(
  first: readonly number[],
  second: readonly number[],
  alternative: Alternative = "two-sided"
): KsOutcome<KsTwoSampleResult> {
  const m = first.length;
  const n = second.length;
  if (m < 1 || n < 1) {
    return { ok: false, reason: "too-few-values" };
  }
  const scan = scanPooled(first, second);
  const { dPlus, dMinus } = scan;
  const d = {
    "two-sided": Math.max(dPlus, dMinus),
    greater: dPlus,
    less: dMinus,
  }[alternative];
  const useMinus =
    alternative === "less" || (alternative === "two-sided" && dMinus > dPlus);
  const location = useMinus ? scan.atMinus : scan.atPlus;
  const ecdf = (sample: readonly number[]) =>
    sample.filter((x) => x <= location).length / sample.length;
  let p: number;
  let method: KsMethod;
  if (m * n <= EXACT_TWO_SAMPLE_CELLS) {
    const q = (0.5 + Math.floor(d * m * n - 1e-7)) / (m * n);
    p = 1 - latticeInsideProbability(q, m, n, scan.blockEnd, alternative);
    method = "exact";
  } else {
    const effective = (m * n) / (m + n);
    p =
      alternative === "two-sided"
        ? kolmogorovLimitUpper(Math.sqrt(effective) * d)
        : Math.exp(-2 * effective * d * d);
    method = "asymptotic";
  }
  return {
    ok: true,
    result: {
      m,
      n,
      dPlus,
      dMinus,
      d,
      alternative,
      p: Math.min(1, Math.max(0, p)),
      method,
      hasTies: scan.hasTies,
      atX: location,
      ecdfFirst: ecdf(first),
      ecdfSecond: ecdf(second),
    },
  };
}

/* ---------------------------------------------------------------- Lilliefors */

/**
 * Approximate p-value of the Lilliefors test (a KS test of normality with the mean and
 * SD estimated from the sample), by the analytic approximation of Dallal & Wilkinson
 * (1986) below p = .10 and the polynomial fit of Molin & Abdi (1998) above it — the
 * formulas used by R's `nortest::lillie.test`. Accurate to about 0.01 in p.
 */
export function lillieforsP(d: number, n: number): number {
  const scaled = n > 100 ? d * (n / 100) ** 0.49 : d;
  const size = n > 100 ? 100 : n;
  const shifted = size + 2.780_19;
  const small = Math.exp(
    -7.012_56 * scaled * scaled * shifted +
      2.995_87 * scaled * Math.sqrt(shifted) -
      0.122_119 +
      0.974_598 / Math.sqrt(size) +
      1.679_97 / size
  );
  if (small <= 0.1) {
    return Math.min(1, small);
  }
  const kk = (Math.sqrt(n) - 0.01 + 0.85 / Math.sqrt(n)) * d;
  if (kk <= 0.302) {
    return 1;
  }
  if (kk <= 0.5) {
    return Math.min(
      1,
      2.767_73 -
        19.828_315 * kk +
        80.709_644 * kk ** 2 -
        138.551_52 * kk ** 3 +
        81.218_052 * kk ** 4
    );
  }
  if (kk <= 0.9) {
    return (
      -4.901_232 +
      40.662_806 * kk -
      97.490_286 * kk ** 2 +
      94.029_866 * kk ** 3 -
      32.355_711 * kk ** 4
    );
  }
  if (kk <= 1.31) {
    return (
      6.198_765 -
      19.558_097 * kk +
      23.186_922 * kk ** 2 -
      12.234_627 * kk ** 3 +
      2.423_045 * kk ** 4
    );
  }
  return 0;
}

/** Sample mean and SD (n − 1) of the data, for the "estimate from sample" option. */
export function normalFromSample(values: readonly number[]): {
  mean: number;
  sd: number;
} {
  const m = mean(values);
  const ss = values.reduce((acc, x) => acc + (x - m) ** 2, 0);
  return { mean: m, sd: Math.sqrt(ss / (values.length - 1)) };
}
